import Image, { StaticImageData } from "next/image"
import Link from "next/link"
import { Star } from "lucide-react"
import { cn, formatDisplayPrice, mapCurrencyToSign } from '@/utils/index'
import { LocationData } from "@/app/stores/places"

interface ProductCardProps {
  id: string
  name: string  
  location: LocationData,
  featuredImage: { url: string | StaticImageData; altText: string }
  minPrice: number
  variants: Array<{ name: string; price: number; sku: string }>
  avgRating: number
  totalReviews: number
  priority?: boolean
  prefetch?: boolean
  className?: string
}

export const ProductCard = ({
  id,
  name,
  featuredImage,
  minPrice,
  avgRating,
  totalReviews,
  location,
  priority = false,
  prefetch = false,
  className,
}: ProductCardProps) => {
  const href = `/product/${id}`
  const linkAria = `Visit product: ${name}`

  return (
    <Link
      className={cn(
        "group flex h-full w-full flex-col overflow-hidden transition-all hover:shadow-md",
        className
      )}
      aria-label={linkAria}
      href={href}
      prefetch={prefetch}
    >
      {/* Product Image */}
      <div className="relative aspect-square overflow-hidden rounded-t-md">
        <Image
          priority={priority}
          className="bg-slate-100 object-cover transition-transform group-hover:z-40 group-hover:scale-105"
          src={featuredImage.url}
          alt={featuredImage.altText}
          fill
        />
      </div>

      {/* Product Details */}
      <div className="flex shrink-0 grow flex-col gap-2 p-4">
        <h3 className="line-clamp-2 text-lg font-semibold transition-colors group-hover:text-orange-500 capitalize">{name}</h3>
        <div className="mt-auto flex flex-col gap-1">
          <span className="flex items-center truncate">
            <svg
              className="w-4 h-4 mr-1 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="truncate">{location?.city || location?.state || location?.country || "Location not specified"}</span>
          </span>

          {/* Rating and Reviews */}
          {!!avgRating && !!totalReviews && (
            <div className="flex items-center space-x-1">
              <Star className="size-4 fill-yellow-400 stroke-yellow-500" />
              <span className="text-sm">{avgRating.toFixed(2)}</span>
              <span className="text-xs">
                ({totalReviews} review{totalReviews !== 1 && "s"})
              </span>
            </div>
          )}

          {/* Price */}
          {!!minPrice && <span>From {formatDisplayPrice(minPrice)}</span>}
        </div>
      </div>
    </Link>
  )
}
